import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import LatexText from "@/components/LatexText";
import { toast } from "sonner";
import {
  Clock, Flag, ChevronLeft, ChevronRight, Send, Video, VideoOff, ShieldAlert,
  AlertTriangle, MessageSquare, X, Save,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/** Load face-api.js from CDN once */
function loadFaceApi() {
  return new Promise((resolve) => {
    if (window.faceapi) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
    s.onload = async () => {
      try {
        // Use tiny face detector with model files from a CDN
        const MODEL_URL = "https://justadudewhohacks.github.io/face-api.js/models";
        await window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        resolve(true);
      } catch (e) { /* noop */ }
    };
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function ExamAttemptPage() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [activeQIdx, setActiveQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [webcamOn, setWebcamOn] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [warnings, setWarnings] = useState([]);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const faceApiReady = useRef(false);
  const submittedRef = useRef(false);

  const currentQuestion = useMemo(() => {
    if (!session) return null;
    return session.sections?.[activeSection]?.questions?.[activeQIdx];
  }, [session, activeSection, activeQIdx]);

  const allQuestions = useMemo(() => {
    if (!session) return [];
    return session.sections.flatMap((s, si) => s.questions.map((q, qi) => ({ ...q, sIdx: si, qIdx: qi })));
  }, [session]);

  // === Load session ===
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/exams/sessions/${sessionId}`);
        setSession(data);
        setAnswers(data.answers || {});
        setFlagged(data.flagged || []);
        const ends = new Date(data.ends_at).getTime();
        setTimeLeft(Math.max(0, Math.floor((ends - Date.now()) / 1000)));
      } catch (e) {
        toast.error("Failed to load session");
        nav("/exam");
      }
    })();
    // eslint-disable-next-line
  }, [sessionId]);

  // === Timer ===
  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => {
      setTimeLeft((s) => {
        if (s <= 1) { clearInterval(t); autoSubmit(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [session]);

  // === Fullscreen + Anti-cheat ===
  const logIncident = useCallback(async (type, detail) => {
    try {
      await api.post("/exams/proctoring/incident", { session_id: sessionId, type, detail });
      setWarnings((w) => [{ type, detail, ts: Date.now() }, ...w].slice(0, 10));
    } catch (e) { /* noop */ }
  }, [sessionId]);

  useEffect(() => {
    if (!session) return;
    // Enter fullscreen
    const goFs = async () => {
      try {
        if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
      } catch (e) { /* noop */ }
    };
    goFs();

    const onFsChange = () => {
      if (!document.fullscreenElement && !submittedRef.current) {
        logIncident("fullscreen_exit", "User exited fullscreen");
      }
    };
    const onVisibility = () => {
      if (document.hidden && !submittedRef.current) logIncident("tab_switch", "Tab hidden");
    };
    const onBlur = () => { if (!submittedRef.current) logIncident("window_blur", "Window lost focus"); };
    const onCopy = (e) => { e.preventDefault(); logIncident("copy_paste", "Copy attempted"); };
    const onPaste = (e) => { e.preventDefault(); logIncident("copy_paste", "Paste attempted"); };
    const onContext = (e) => { e.preventDefault(); };
    const onKey = (e) => {
      // Block PrintScreen (best-effort) and Alt+Tab (limited)
      if (e.key === "PrintScreen") { navigator.clipboard?.writeText(""); logIncident("copy_paste", "PrintScreen"); }
    };

    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("contextmenu", onContext);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("keydown", onKey);
    };
  }, [session, logIncident]);

  // === Webcam + face-api ===
  useEffect(() => {
    if (!session || !session.proctoring_enabled) return;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setWebcamOn(true);
        }
        faceApiReady.current = await loadFaceApi();
      } catch (e) {
        toast.error("Camera permission denied — proctoring disabled");
        logIncident("no_face", "Camera not accessible");
      }
    })();
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line
  }, [session]);

  // Face detection loop every 5s
  useEffect(() => {
    if (!session || !webcamOn) return;
    const interval = setInterval(async () => {
      try {
        if (!faceApiReady.current || !videoRef.current || !window.faceapi) return;
        const detections = await window.faceapi.detectAllFaces(
          videoRef.current,
          new window.faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 })
        );
        if (detections.length === 0) logIncident("no_face", "No face detected");
        else if (detections.length > 1) logIncident("multi_face", `${detections.length} faces detected`);

        // Snapshot every ~30s (every 6th tick)
        if (Math.random() < 0.15) {
          const canvas = canvasRef.current || document.createElement("canvas");
          canvas.width = 160; canvas.height = 120;
          canvas.getContext("2d").drawImage(videoRef.current, 0, 0, 160, 120);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.5);
          api.post("/exams/proctoring/snapshot", {
            session_id: sessionId, image_b64: dataUrl, detected_faces: detections.length,
          }).catch(()=>{});
        }
      } catch (e) { /* noop */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [webcamOn, session, sessionId, logIncident]);

  // === Answer handlers ===
  const saveAnswer = async (qid, ans, isFlag) => {
    setAnswers((p) => ({ ...p, [qid]: ans }));
    try {
      await api.post(`/exams/sessions/${sessionId}/answer`, {
        question_id: qid, answer: ans, flagged: isFlag,
      });
    } catch (e) { /* noop */ }
  };

  const toggleFlag = async (qid) => {
    const has = flagged.includes(qid);
    const newFlagged = has ? flagged.filter(x => x !== qid) : [...flagged, qid];
    setFlagged(newFlagged);
    await api.post(`/exams/sessions/${sessionId}/answer`, {
      question_id: qid, answer: answers[qid] ?? "", flagged: !has,
    });
  };

  const submit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await api.post(`/exams/sessions/${sessionId}/submit`, { auto });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (document.fullscreenElement) await document.exitFullscreen().catch(()=>{});
      toast.success("Test submitted");
      nav(`/exam/result/${session.test_id}`);
    } catch (e) {
      submittedRef.current = false;
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  const autoSubmit = () => { submit(true); };

  // === Chat polling ===
  useEffect(() => {
    if (!session) return;
    const load = async () => {
      try {
        const { data } = await api.get(`/exams/chat/${sessionId}`);
        setChatMessages(data);
      } catch (e) { /* noop */ }
    };
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [session, sessionId]);

  const sendChat = async () => {
    if (!chatText.trim()) return;
    await api.post("/exams/chat", { session_id: sessionId, message: chatText });
    setChatText("");
    const { data } = await api.get(`/exams/chat/${sessionId}`);
    setChatMessages(data);
  };

  if (!session) return <div className="p-10">Loading exam…</div>;

  const totalQ = allQuestions.length;
  const answeredCount = Object.keys(answers).filter(k => answers[k] !== "" && answers[k] != null && (Array.isArray(answers[k]) ? answers[k].length : true)).length;
  const flaggedCount = flagged.length;
  const remaining = totalQ - answeredCount;
  const fmt = (s) => {
    const m = Math.floor(s / 60), sec = s % 60;
    const h = Math.floor(m / 60);
    return h > 0 ? `${h}:${String(m%60).padStart(2,'0')}:${String(sec).padStart(2,'0')}` : `${m}:${String(sec).padStart(2,'0')}`;
  };
  const timeLow = timeLeft < 300; // <5 min

  const goto = (sIdx, qIdx) => { setActiveSection(sIdx); setActiveQIdx(qIdx); };
  const next = () => {
    const sec = session.sections[activeSection];
    if (activeQIdx < sec.questions.length - 1) setActiveQIdx(activeQIdx + 1);
    else if (activeSection < session.sections.length - 1) { setActiveSection(activeSection + 1); setActiveQIdx(0); }
  };
  const prev = () => {
    if (activeQIdx > 0) setActiveQIdx(activeQIdx - 1);
    else if (activeSection > 0) { setActiveSection(activeSection - 1); setActiveQIdx(session.sections[activeSection - 1].questions.length - 1); }
  };

  const renderInput = () => {
    if (!currentQuestion) return null;
    const q = currentQuestion;
    const val = answers[q.id];

    if (q.type === "mcq_single" || q.type === "true_false") {
      return (
        <div className="space-y-2">
          {q.options.map((opt, i) => {
            const chosen = val === opt;
            return (
              <label key={i} data-testid={`opt-${i}`}
                className={`flex items-center gap-3 p-3 rounded-md cursor-pointer border ${chosen ? 'border-[#0A66C2] bg-[#EAF3FB]' : 'border-[#E0DFDC] hover:bg-[#F4F2EE]'}`}>
                <input type="radio" checked={chosen} onChange={() => saveAnswer(q.id, opt)} className="accent-[#0A66C2]" />
                <span className="text-sm text-[#000000E6] flex-1"><LatexText>{opt}</LatexText></span>
              </label>
            );
          })}
        </div>
      );
    }
    if (q.type === "mcq_multi") {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="space-y-2">
          {q.options.map((opt, i) => {
            const chosen = arr.includes(opt);
            return (
              <label key={i} data-testid={`opt-${i}`}
                className={`flex items-center gap-3 p-3 rounded-md cursor-pointer border ${chosen ? 'border-[#0A66C2] bg-[#EAF3FB]' : 'border-[#E0DFDC] hover:bg-[#F4F2EE]'}`}>
                <input type="checkbox" checked={chosen} onChange={() => {
                  const na = chosen ? arr.filter(x => x !== opt) : [...arr, opt];
                  saveAnswer(q.id, na);
                }} className="accent-[#0A66C2]" />
                <span className="text-sm text-[#000000E6] flex-1"><LatexText>{opt}</LatexText></span>
              </label>
            );
          })}
        </div>
      );
    }
    if (q.type === "numerical" || q.type === "integer" || q.type === "fill_blank") {
      return (
        <input
          type={q.type === "integer" ? "number" : "text"}
          value={val || ""}
          onChange={(e) => saveAnswer(q.id, e.target.value)}
          className="w-full max-w-md px-4 py-2.5 border border-[#E0DFDC] rounded-md text-lg"
          placeholder="Type your answer"
          data-testid="numerical-input"
        />
      );
    }
    // subjective (short/long/essay)
    return (
      <textarea
        value={val || ""}
        onChange={(e) => saveAnswer(q.id, e.target.value)}
        placeholder="Write your answer (will be graded manually)"
        className="w-full min-h-[180px] px-3 py-2 border border-[#E0DFDC] rounded-md"
        data-testid="subjective-input"
      />
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Sticky header with timer + test name */}
      <header className="bg-white border-b border-[#E0DFDC] sticky top-0 z-40" data-testid="exam-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#000000E6] truncate" data-testid="exam-title">{session.test_name}</div>
            <div className="text-[11px] text-[#00000099] truncate">{session.student_name} · {answeredCount}/{totalQ} answered · {flaggedCount} flagged</div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`px-4 py-1.5 rounded-full font-mono text-sm font-bold ${timeLow ? 'bg-[#F8D7DA] text-[#CC1016] animate-pulse' : 'bg-[#EAF3FB] text-[#0A66C2]'}`} data-testid="exam-timer">
              <Clock size={14} className="inline mr-1"/> {fmt(timeLeft)}
            </div>
            <button onClick={() => setChatOpen(true)} data-testid="chat-btn" className="p-2 rounded-full hover:bg-[#00000010] relative">
              <MessageSquare size={16}/>
              {chatMessages.length > 0 && <span className="absolute -top-1 -right-1 text-[9px] bg-[#0A66C2] text-white rounded-full w-4 h-4 flex items-center justify-center">{chatMessages.length}</span>}
            </button>
            <button onClick={() => setConfirmSubmit(true)} data-testid="submit-exam-btn" className="btn-primary inline-flex items-center gap-2"><Send size={14}/> Submit</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: question */}
        <div className="lg:col-span-3 space-y-4">
          {/* Section tabs */}
          <div className="flex gap-2 flex-wrap" data-testid="section-tabs">
            {session.sections.map((s, i) => (
              <button key={i} onClick={() => { setActiveSection(i); setActiveQIdx(0); }} data-testid={`section-tab-${i}`}
                className={`text-sm px-4 py-2 rounded-full font-semibold ${activeSection === i ? 'bg-[#0A66C2] text-white' : 'bg-white border border-[#E0DFDC] text-[#000000E6] hover:bg-[#F4F2EE]'}`}>
                {s.name} ({s.questions.length})
              </button>
            ))}
          </div>

          <div className="linkedin-card p-6" data-testid="question-panel">
            {currentQuestion && (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#00000099]">Question {activeQIdx + 1} · {currentQuestion.type} · {currentQuestion.marks} mark{currentQuestion.marks > 1 ? "s" : ""}{currentQuestion.negative_marks > 0 ? ` · -${currentQuestion.negative_marks} negative` : ""}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#00000066] mt-0.5">{currentQuestion.subject} · {currentQuestion.chapter} · {currentQuestion.difficulty}</div>
                  </div>
                  <button onClick={() => toggleFlag(currentQuestion.id)} data-testid="flag-btn"
                    className={`text-xs px-3 py-1 rounded-full font-semibold inline-flex items-center gap-1 ${flagged.includes(currentQuestion.id) ? 'bg-[#FFF3CD] text-[#915907] border border-[#915907]/30' : 'bg-white border border-[#E0DFDC] text-[#00000099] hover:bg-[#F4F2EE]'}`}>
                    <Flag size={12}/> {flagged.includes(currentQuestion.id) ? 'Flagged' : 'Flag for review'}
                  </button>
                </div>
                <div className="text-[16px] text-[#000000E6] leading-relaxed mb-6" data-testid="question-text">
                  <LatexText>{currentQuestion.text}</LatexText>
                </div>
                {(currentQuestion.attachments || []).map((a, i) => (
                  <img key={i} src={a} alt="attachment" className="max-h-64 rounded-md border border-[#E0DFDC] mb-4"/>
                ))}
                {renderInput()}
              </>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button onClick={prev} disabled={activeSection === 0 && activeQIdx === 0} data-testid="prev-btn"
              className="px-4 py-2 rounded-full border border-[#00000033] text-[#000000E6] font-semibold disabled:opacity-30 inline-flex items-center gap-2"><ChevronLeft size={14}/> Previous</button>
            <button onClick={next} data-testid="save-next-btn" className="btn-primary inline-flex items-center gap-2"><Save size={14}/> Save & Next <ChevronRight size={14}/></button>
          </div>
        </div>

        {/* Right: Palette + webcam */}
        <div className="space-y-4">
          <div className="linkedin-card p-4" data-testid="webcam-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#00000099]">Proctoring</div>
              {webcamOn ? <span className="text-[10px] text-[#057642] flex items-center gap-1"><Video size={10}/> Live</span> : <span className="text-[10px] text-[#CC1016] flex items-center gap-1"><VideoOff size={10}/> Off</span>}
            </div>
            <video ref={videoRef} muted autoPlay playsInline className="w-full aspect-video bg-black rounded-md" data-testid="webcam-video" />
            <canvas ref={canvasRef} className="hidden"/>
            {warnings.length > 0 && (
              <div className="mt-2 text-[11px] text-[#CC1016] flex items-start gap-1"><ShieldAlert size={12} className="mt-0.5"/><div>{warnings.length} incident(s) logged</div></div>
            )}
          </div>

          <div className="linkedin-card p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#00000099] mb-2">Question palette</div>
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {session.sections.map((s, si) => (
                <div key={si}>
                  <div className="text-[11px] font-semibold text-[#00000099] mb-1">{s.name}</div>
                  <div className="grid grid-cols-6 gap-1">
                    {s.questions.map((q, qi) => {
                      const status = answers[q.id] != null && answers[q.id] !== "" && (Array.isArray(answers[q.id]) ? answers[q.id].length : true)
                        ? "answered" : "unattempted";
                      const isFlag = flagged.includes(q.id);
                      const isActive = activeSection === si && activeQIdx === qi;
                      let cls = "bg-white text-[#00000099] border-[#E0DFDC]";
                      if (isActive) cls = "bg-[#0A66C2] text-white border-[#0A66C2] ring-2 ring-[#0A66C2]/30";
                      else if (isFlag && status === "answered") cls = "bg-[#5A2A82] text-white border-[#5A2A82]";
                      else if (isFlag) cls = "bg-[#FFF3CD] text-[#915907] border-[#915907]/30";
                      else if (status === "answered") cls = "bg-[#D1E7DD] text-[#057642] border-[#057642]/30";
                      return (
                        <button key={qi} onClick={() => goto(si, qi)} data-testid={`palette-${si}-${qi}`}
                          className={`text-[11px] font-semibold w-9 h-9 rounded border ${cls}`}>
                          {qi + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[#E0DFDC] grid grid-cols-2 gap-1 text-[10px] text-[#00000099]">
              <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#D1E7DD] border border-[#057642]/30"/> Answered</div>
              <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FFF3CD] border border-[#915907]/30"/> Flagged</div>
              <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-[#E0DFDC]"/> Not attempted</div>
              <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#5A2A82]"/> Answered + Flag</div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit confirmation */}
      <Dialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Submit test?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#00000099] space-y-2">
            <div>Answered: <span className="font-semibold text-[#057642]">{answeredCount}</span> / {totalQ}</div>
            <div>Unattempted: <span className="font-semibold text-[#CC1016]">{remaining}</span></div>
            <div>Flagged: <span className="font-semibold text-[#915907]">{flaggedCount}</span></div>
            <div className="pt-2 text-xs">Once submitted, you cannot re-enter this test.</div>
          </div>
          <DialogFooter>
            <button onClick={() => setConfirmSubmit(false)} className="px-4 py-2 rounded-full border border-[#00000033]" data-testid="cancel-submit-btn">Continue test</button>
            <button onClick={() => { setConfirmSubmit(false); submit(false); }} disabled={submitting} className="btn-primary" data-testid="confirm-submit-btn">{submitting ? "Submitting…" : "Submit final"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat panel */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invigilator chat</DialogTitle></DialogHeader>
          <div className="min-h-[240px] max-h-[400px] overflow-y-auto space-y-2 p-2 bg-[#F4F2EE] rounded-md" data-testid="chat-messages">
            {chatMessages.length === 0 && <div className="text-xs text-[#00000066] text-center py-6">No messages yet. Type below to reach the invigilator.</div>}
            {chatMessages.map((m) => {
              const mine = m.from_role === "student";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[70%] rounded-md px-3 py-2 text-sm ${mine ? 'bg-[#0A66C2] text-white' : 'bg-white border border-[#E0DFDC]'}`}>
                    <div className="text-[10px] opacity-75 uppercase tracking-wider">{m.from_name}</div>
                    <div>{m.message}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-2">
            <input value={chatText} onChange={(e)=>setChatText(e.target.value)} onKeyDown={(e)=>e.key === 'Enter' && sendChat()}
              placeholder="Type message…" data-testid="chat-input" className="flex-1 px-3 py-2 border border-[#E0DFDC] rounded-md text-sm"/>
            <button onClick={sendChat} data-testid="chat-send-btn" className="btn-primary">Send</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
